import { useCallback, useEffect, useRef, useState } from 'react';
import { reportApi } from '../../../../api/reportApi';
import { sessionsApi } from '../../../../api/sessionsApi';
import { getTranscriptionLanguageName } from '../../../../constants/transcriptionLanguages';
import { REPORT_LLM_MODEL, reportModelLabel } from '../../report/utils/reportConfig';
import { normalizeSession } from '../utils/normalizeSession';
import { glanceMarkdownFromReport, speakersMarkdownFromReport } from '../utils/reportFromFile';
import { useReportTranslations } from './useReportTranslations';
import { isRunningStatus } from '../../../../constants/jobStatus';

const POLL_MS = 3000;
const QA_POLL_TIMEOUT_MS = 5 * 60 * 1000;

const EMPTY_MARKDOWN = { glance: '', full: '', speakers: '', inDepth: '' };
const EMPTY_ENRICHMENT = { transcriptReferences: null, sourceReferences: null, relatedResources: [], factualNotes: [] };

const fileData = (report) => report?.data?.data || report?.data;

/** Citations and knowledge-base extras of a report file; fields the file lacks keep their value. */
const mergeEnrichment = (prev, data) => ({
  transcriptReferences: data?.transcript_references || prev.transcriptReferences,
  sourceReferences: data?.source_references || prev.sourceReferences,
  relatedResources: Array.isArray(data?.related_resources) ? data.related_resources : prev.relatedResources,
  factualNotes: Array.isArray(data?.factual_notes) ? data.factual_notes : prev.factualNotes,
});

/** The report fields stored on the session (manual edits or a translation). */
const markdownOf = (session) => ({
  glance: session.session_at_glance_md,
  full: session.full_report_md,
  speakers: session.speakers_md,
  inDepth: session.in_depth_md,
});

/** Generation request from the config modal; without a config, the defaults. */
const generationOptions = (config) => ({
  llm_model: config?.llm_model || REPORT_LLM_MODEL,
  deeplinks_enabled: config?.deeplinks_enabled !== false,
  in_depth_enabled: config?.in_depth_enabled !== false,
  report_pipeline: config?.report_pipeline === 'v3' || config?.report_pipeline === 'v4-s55'
    ? config.report_pipeline
    : 'v1',
  generate_qa: Boolean(config?.generate_qa),
  questions: config?.questions || [],
  custom_prompts: config?.custom_prompts || null,
});

/**
 * The official session report: generation status, content (session markdown
 * fields, falling back to the report file), translations and Q&A.
 */
export function useSessionReport(id, primaryLanguage, notify, reloadSession) {
  const [status, setStatus] = useState(null);
  const [data, setData] = useState(null);
  const [markdown, setMarkdown] = useState(EMPTY_MARKDOWN);
  const [enrichment, setEnrichment] = useState(EMPTY_ENRICHMENT);
  const [graphUrl, setGraphUrl] = useState('');
  const [hasReportFile, setHasReportFile] = useState(false);
  const [lang, setLang] = useState(primaryLanguage);
  const [isStarting, setIsStarting] = useState(false);
  const [isRegeneratingQa, setIsRegeneratingQa] = useState(false);

  const loadGeneration = useRef(0);
  const prevProgress = useRef(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    setLang(primaryLanguage);
  }, [primaryLanguage]);

  const {
    languages,
    translatingCode,
    loadLanguages,
    translate,
    cancelTranslation,
    clearLanguages,
  } = useReportTranslations(id, notify);

  const loadStatus = useCallback(async () => {
    const generation = ++loadGeneration.current;
    const isCurrent = () => alive.current && generation === loadGeneration.current;
    try {
      const next = await reportApi.getSessionReportStatus(id);
      if (!isCurrent()) return;
      setStatus(next);

      const progress = next?.progress ?? 0;
      if (prevProgress.current != null && prevProgress.current < 100 && progress >= 100 && next?.status === 'COMPLETED') {
        notify({ type: 'success', title: 'Report complete', message: 'Report generation finished.' });
        loadLanguages();
      }
      prevProgress.current = progress;

      if (next.status !== 'COMPLETED') {
        setData(null);
        return;
      }

      try {
        // Session fields hold manual edits, so they win over the generated file.
        const session = normalizeSession(await sessionsApi.get(id));
        if (!isCurrent()) return;
        const stored = markdownOf(session);
        setMarkdown((prev) => ({
          glance: stored.glance || prev.glance,
          full: stored.full || prev.full,
          speakers: stored.speakers || prev.speakers,
          inDepth: stored.inDepth || prev.inDepth,
        }));

        const report = await reportApi.getSessionReport(id);
        if (!isCurrent()) return;
        const file = fileData(report);
        const sessionReport = report.data?.['Session report'];
        setData(file);
        setEnrichment((prev) => mergeEnrichment(prev, file));
        setMarkdown((prev) => ({
          ...prev,
          glance: stored.glance || glanceMarkdownFromReport(sessionReport) || prev.glance,
          full: stored.full || sessionReport?.['Full session report'] || prev.full,
          speakers: stored.speakers || speakersMarkdownFromReport(report.data?.Speakers) || prev.speakers,
        }));
        if (report.path) setHasReportFile(true);
        const graph = report.graph_path || file?.graph_path;
        if (graph) setGraphUrl(graph);
      } catch (e) {
        // Keep what is on screen on transient errors.
        console.error('Failed to load report content:', e);
      }
    } catch (e) {
      if (!isCurrent()) return;
      if (e.status === 404) {
        setStatus(null);
        setData(null);
      } else {
        console.error('Failed to load report status:', e);
      }
    }
  }, [id, notify, loadLanguages]);

  useEffect(() => {
    loadStatus();
    loadLanguages();
  }, [loadStatus, loadLanguages]);

  // Poll while generating, or while a finished report still waits for its auto-translations.
  const awaitingTranslations = status?.status === 'COMPLETED' && (status.progress ?? 100) < 100;
  const busy = isRunningStatus(status?.status) || awaitingTranslations;
  useEffect(() => {
    if (!busy) return undefined;
    const interval = setInterval(() => {
      loadStatus();
      if (awaitingTranslations) loadLanguages();
    }, POLL_MS);
    return () => clearInterval(interval);
  }, [busy, awaitingTranslations, loadStatus, loadLanguages]);

  // A tab left in the background can come back with its content dropped; reload it then.
  const stateRef = useRef({});
  stateRef.current = { status, data, markdown };
  useEffect(() => {
    const onVisible = () => {
      if (document.hidden) return;
      const { status: s, data: d, markdown: md } = stateRef.current;
      if (s?.status === 'COMPLETED' && !d && !md.glance.trim() && !md.full.trim()) loadStatus();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [loadStatus]);

  const switchLanguage = useCallback(async (next) => {
    if (next === lang) return;
    try {
      if (next === primaryLanguage) {
        await reloadSession();
        await loadStatus();
      } else {
        setMarkdown(markdownOf(normalizeSession(await sessionsApi.get(id, { lang: next }))));
        const report = await reportApi.getSessionReport(id, next);
        setEnrichment((prev) => mergeEnrichment(prev, fileData(report)));
      }
      setLang(next);
    } catch (e) {
      notify({ type: 'error', title: 'Language switch failed', message: e.message });
    }
  }, [id, lang, primaryLanguage, reloadSession, loadStatus, notify]);

  const deleteTranslation = useCallback(async (code) => {
    if (!code || code === primaryLanguage) return;
    try {
      await reportApi.deleteTranslation(id, code);
      if (lang === code) await switchLanguage(primaryLanguage);
      await loadLanguages();
      notify({ type: 'success', title: 'Translation deleted', message: `${getTranscriptionLanguageName(code)} translation removed.` });
    } catch (e) {
      notify({ type: 'error', title: 'Delete failed', message: e.message });
    }
  }, [id, lang, primaryLanguage, switchLanguage, loadLanguages, notify]);

  const exists = status?.status === 'COMPLETED' || hasReportFile;

  const generate = useCallback(async (config = null) => {
    setIsStarting(true);
    prevProgress.current = 0;
    const replacing = exists;
    try {
      if (replacing) {
        // The backend deletes the old report and clears the manual report fields.
        await reportApi.regenerateReport(id);
        setData(null);
        setStatus(null);
        setHasReportFile(false);
        setMarkdown(EMPTY_MARKDOWN);
        setEnrichment(EMPTY_ENRICHMENT);
        setGraphUrl('');
        setLang(primaryLanguage);
        clearLanguages();
      }

      const options = generationOptions(config);
      const response = await reportApi.generateSessionReport(id, options);
      const model = reportModelLabel(options.llm_model);
      const queued = response.status || 'QUEUED';
      notify({
        type: 'success',
        title: replacing ? 'Report regeneration started' : 'Report generation started',
        message: replacing
          ? `Old report deleted. Generating new report with ${model}. Status: ${queued}`
          : `Report generation queued with ${model}. Status: ${queued}`,
      });
      if (replacing) loadLanguages();
      reloadSession();
      setTimeout(() => alive.current && loadStatus(), 1500);
    } catch (e) {
      notify({ type: 'error', title: 'Failed to generate report', message: e.message });
      setStatus((prev) => (prev ? { ...prev, status: 'FAILED' } : null));
    } finally {
      setIsStarting(false);
    }
  }, [id, exists, primaryLanguage, notify, loadLanguages, clearLanguages, loadStatus, reloadSession]);

  const regenerateQa = useCallback(async (questions) => {
    if (!Array.isArray(questions) || questions.length === 0) return;
    setIsRegeneratingQa(true);
    setData((prev) => (prev ? { ...prev, qa_status: 'PROCESSING', qa_error: null } : prev));
    try {
      await reportApi.regenerateSessionQa(id, { questions, deeplinks_enabled: true });
      notify({
        type: 'success',
        title: 'Q&A generation started',
        message: `Analyzing ${questions.length} question(s). This tab will update when ready.`,
      });
    } catch (e) {
      setIsRegeneratingQa(false);
      setData((prev) => (prev ? { ...prev, qa_status: 'FAILED', qa_error: e.message } : prev));
      notify({ type: 'error', title: 'Failed to start Q&A generation', message: e.message });
      return;
    }

    const started = Date.now();
    const poll = async () => {
      if (!alive.current) return;
      try {
        const next = fileData(await reportApi.getSessionReport(id));
        if (next) {
          setData(next);
          if (next.qa_status && next.qa_status !== 'PROCESSING') {
            setIsRegeneratingQa(false);
            if (next.qa_status === 'COMPLETED') {
              notify({ type: 'success', title: 'Q&A ready', message: 'The Q&A analysis has been updated.' });
            } else if (next.qa_status === 'FAILED') {
              notify({ type: 'error', title: 'Q&A failed', message: next.qa_error || 'Q&A generation failed.' });
            }
            return;
          }
        }
      } catch {
        // Keep polling until the timeout on transient errors.
      }
      if (Date.now() - started > QA_POLL_TIMEOUT_MS) {
        setIsRegeneratingQa(false);
        return;
      }
      setTimeout(poll, POLL_MS);
    };
    setTimeout(poll, POLL_MS);
  }, [id, notify]);

  const hasContent = Boolean(data || markdown.glance.trim() || markdown.full.trim());

  return {
    status,
    data,
    markdown,
    enrichment,
    graphUrl,
    exists,
    hasContent,
    busy,
    lang,
    isTranslated: lang !== primaryLanguage,
    languages,
    translatingCode,
    isStarting,
    isRegeneratingQa,
    switchLanguage,
    translate,
    cancelTranslation,
    deleteTranslation,
    generate,
    regenerateQa,
  };
}
